import React from 'react';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, useTheme, type SxProps, type Theme } from '@mui/material/styles';
import type { ButtonProps } from '@mui/material/Button';

export const acceptanceDemoTokens = {
  colors: {
    background: '#08101f',
    backgroundDeep: '#060b16',
    backgroundPanel: '#0b1221',
    surface: 'rgba(255,255,255,0.042)',
    surfaceElevated: 'rgba(255,255,255,0.06)',
    border: 'rgba(104, 134, 191, 0.18)',
    borderStrong: 'rgba(123, 162, 237, 0.28)',
    textPrimary: '#f8fbff',
    textSecondary: 'rgba(244, 249, 255, 0.82)',
    textMuted: 'rgba(165, 186, 218, 0.72)',
    blue: '#2f8cff',
    blueSoft: '#6db1ff',
    orange: '#ff9a3d',
    orangeSoft: '#ffbf7b',
  },
  radius: {
    panel: 4,
    card: 3,
    icon: 2.5,
    chip: 999,
  },
  shadow: {
    panel: '0 28px 90px rgba(0,0,0,0.28)',
    card: '0 18px 48px rgba(0,0,0,0.24)',
    glowBlue: '0 0 0 1px rgba(47,140,255,0.14), 0 20px 48px rgba(20, 88, 180, 0.22)',
    glowOrange: '0 0 0 1px rgba(255,154,61,0.16), 0 20px 48px rgba(188, 101, 18, 0.22)',
  },
} as const;

export type AcceptanceDemoAccent = 'neutral' | 'blue' | 'orange';
export type AcceptanceDemoPanelTone = 'default' | 'hero' | 'blue' | 'orange' | 'subtle';

type AcceptanceDemoPageShellProps = {
  children: React.ReactNode;
};

type AcceptanceDemoPanelProps = {
  children: React.ReactNode;
  tone?: AcceptanceDemoPanelTone;
  sx?: SxProps<Theme>;
};

type AcceptanceDemoBadgeProps = {
  label: string;
  accent?: AcceptanceDemoAccent;
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
};

type AcceptanceDemoButtonAction = {
  label: string;
  onClick: () => void;
  accent?: AcceptanceDemoAccent;
  variant?: ButtonProps['variant'];
};

type AcceptanceDemoHeroProps = {
  eyebrow: string;
  title: string;
  subtitle: string;
  contextLine?: string;
  supportingText?: string;
  illustration?: React.ReactNode;
  compactTitle?: boolean;
  badges?: Array<{
    label: string;
    accent?: AcceptanceDemoAccent;
    icon?: React.ReactNode;
  }>;
  primaryAction?: AcceptanceDemoButtonAction;
  secondaryAction?: AcceptanceDemoButtonAction;
  tertiaryAction?: AcceptanceDemoButtonAction;
};

type AcceptanceDemoSectionHeaderProps = {
  eyebrow: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  sx?: SxProps<Theme>;
};

type AcceptanceDemoIconContainerProps = {
  accent?: AcceptanceDemoAccent;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

type AcceptanceDemoStatCardProps = {
  eyebrow?: string;
  title: string;
  detail: string;
  accent?: AcceptanceDemoAccent;
  icon?: React.ReactNode;
  ctaLabel?: string;
  onClick?: () => void;
  onCtaClick?: () => void;
  minHeight?: number | string | { xs?: number | string; sm?: number | string; md?: number | string; lg?: number | string; xl?: number | string };
  sx?: SxProps<Theme>;
};

function getAccentColor(accent: AcceptanceDemoAccent): string {
  if (accent === 'orange') return acceptanceDemoTokens.colors.orange;
  if (accent === 'blue') return acceptanceDemoTokens.colors.blue;
  return 'rgba(255,255,255,0.82)';
}

function getAccentSoftColor(accent: AcceptanceDemoAccent): string {
  if (accent === 'orange') return acceptanceDemoTokens.colors.orangeSoft;
  if (accent === 'blue') return acceptanceDemoTokens.colors.blueSoft;
  return acceptanceDemoTokens.colors.textSecondary;
}

function getPanelToneSx(tone: AcceptanceDemoPanelTone, theme: Theme): SxProps<Theme> {
  const blue = acceptanceDemoTokens.colors.blue;
  const orange = acceptanceDemoTokens.colors.orange;

  switch (tone) {
    case 'hero':
      return {
        position: 'relative',
        overflow: 'hidden',
        background: `
          linear-gradient(180deg, rgba(12,20,36,0.96) 0%, rgba(8,14,27,0.98) 100%),
          linear-gradient(135deg, ${alpha(blue, 0.07)} 0%, rgba(255,255,255,0) 58%)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -90,
          right: '30%',
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(blue, 0.16)} 0%, rgba(47,140,255,0) 74%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: -60,
          top: -10,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(orange, 0.2)} 0%, rgba(255,154,61,0) 72%)`,
          pointerEvents: 'none',
        },
      };
    case 'blue':
      return {
        background: `linear-gradient(180deg, ${alpha(blue, 0.14)} 0%, rgba(255,255,255,0.03) 100%)`,
        borderColor: alpha(blue, 0.28),
        boxShadow: acceptanceDemoTokens.shadow.glowBlue,
      };
    case 'orange':
      return {
        background: `linear-gradient(180deg, rgba(255,255,255,0.07) 0%, ${alpha(orange, 0.16)} 100%)`,
        borderColor: alpha(orange, 0.28),
        boxShadow: acceptanceDemoTokens.shadow.glowOrange,
      };
    case 'subtle':
      return {
        background: 'rgba(255,255,255,0.022)',
        borderColor: acceptanceDemoTokens.colors.border,
      };
    default:
      return {
        background: `linear-gradient(180deg, ${acceptanceDemoTokens.colors.surfaceElevated} 0%, ${acceptanceDemoTokens.colors.surface} 100%)`,
        borderColor: acceptanceDemoTokens.colors.border,
        boxShadow: '0 16px 40px rgba(0,0,0,0.22)',
        backdropFilter: `blur(${theme.spacing(1.75)})`,
      };
  }
}

export const AcceptanceDemoPageShell: React.FC<AcceptanceDemoPageShellProps> = ({ children }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 'none',
        minHeight: '100vh',
        mx: { xs: -2, sm: -3 },
        mt: { xs: -2, sm: -3 },
        px: { xs: 2, sm: 3, lg: 5 },
        py: { xs: 3, sm: 4, lg: 5 },
        color: acceptanceDemoTokens.colors.textPrimary,
        bgcolor: acceptanceDemoTokens.colors.backgroundDeep,
        backgroundImage: `
          radial-gradient(circle at top left, ${alpha(theme.palette.primary.main || acceptanceDemoTokens.colors.blue, 0.16)}, transparent 28%),
          radial-gradient(circle at 88% 10%, ${alpha(acceptanceDemoTokens.colors.orange, 0.12)}, transparent 24%),
          linear-gradient(180deg, ${acceptanceDemoTokens.colors.background} 0%, ${acceptanceDemoTokens.colors.backgroundDeep} 100%)
        `,
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }}>{children}</Stack>
    </Box>
  );
};

export const AcceptanceDemoPanel: React.FC<AcceptanceDemoPanelProps> = ({ children, tone = 'default', sx }) => {
  const theme = useTheme();

  return (
    <Box
      sx={[
        {
          position: 'relative',
          borderRadius: acceptanceDemoTokens.radius.panel,
          p: { xs: 2.25, sm: 2.75 },
          border: '1px solid',
        },
        getPanelToneSx(tone, theme),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
};

export const AcceptanceDemoIconContainer: React.FC<AcceptanceDemoIconContainerProps> = ({
  accent = 'blue',
  children,
  sx,
}) => {
  const color = getAccentColor(accent);

  return (
    <Box
      sx={[
        {
          width: 44,
          height: 44,
          borderRadius: acceptanceDemoTokens.radius.icon,
          display: 'grid',
          placeItems: 'center',
          color,
          background: `linear-gradient(180deg, ${alpha(color, 0.26)} 0%, ${alpha(color, 0.08)} 100%)`,
          border: `1px solid ${alpha(color, 0.3)}`,
          boxShadow: accent === 'orange' ? acceptanceDemoTokens.shadow.glowOrange : acceptanceDemoTokens.shadow.glowBlue,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
};

export const AcceptanceDemoBadge: React.FC<AcceptanceDemoBadgeProps> = ({
  label,
  accent = 'neutral',
  icon,
  sx,
}) => {
  const color = getAccentColor(accent);
  const softColor = getAccentSoftColor(accent);

  return (
    <Box
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.15,
          py: 0.72,
          borderRadius: acceptanceDemoTokens.radius.chip,
          color: softColor,
          background: `linear-gradient(180deg, ${alpha(color, 0.14)} 0%, rgba(255,255,255,0.04) 100%)`,
          border: `1px solid ${alpha(color, accent === 'neutral' ? 0.16 : 0.28)}`,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.1,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {icon ? (
        <Box sx={{ display: 'inline-flex', fontSize: 16, lineHeight: 0, color }}>
          {icon}
        </Box>
      ) : null}
      <span>{label}</span>
    </Box>
  );
};

export const AcceptanceDemoButton: React.FC<
  ButtonProps & { accent?: AcceptanceDemoAccent; label: string; onClick: () => void }
> = ({ accent = 'blue', label, onClick, sx, variant = 'contained', ...buttonProps }) => {
  const color = getAccentColor(accent);

  return (
    <Button
      {...buttonProps}
      variant={variant}
      onClick={onClick}
      endIcon={buttonProps.endIcon ?? (variant === 'text' ? <ArrowOutwardRoundedIcon /> : undefined)}
      sx={[
        {
          minHeight: 46,
          px: 2,
          borderRadius: 1.6,
          textTransform: 'none',
          fontWeight: 800,
          fontSize: 13.5,
        },
        variant === 'contained'
          ? {
              bgcolor: color,
              color: acceptanceDemoTokens.colors.textPrimary,
              boxShadow:
                accent === 'orange'
                  ? '0 12px 30px rgba(255, 154, 61, 0.34)'
                  : '0 12px 30px rgba(47, 140, 255, 0.28)',
              '&:hover': {
                bgcolor: color,
                filter: 'brightness(1.05)',
              },
            }
          : {},
        variant === 'outlined'
          ? {
              color: acceptanceDemoTokens.colors.textPrimary,
              borderColor: alpha(color, accent === 'neutral' ? 0.18 : 0.34),
              backgroundColor: 'rgba(255,255,255,0.02)',
              '&:hover': {
                borderColor: alpha(color, 0.5),
                backgroundColor: alpha(color, 0.08),
              },
            }
          : {},
        variant === 'text'
          ? {
              px: 0.5,
              color: accent === 'orange' ? acceptanceDemoTokens.colors.orangeSoft : acceptanceDemoTokens.colors.textSecondary,
              '&:hover': {
                backgroundColor: 'transparent',
                color: acceptanceDemoTokens.colors.textPrimary,
              },
            }
          : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {label}
    </Button>
  );
};

export const AcceptanceDemoFlowGraphic: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const blue = acceptanceDemoTokens.colors.blue;
  const orange = acceptanceDemoTokens.colors.orange;
  const nodes = [
    { key: 'detect', icon: <WarningAmberRoundedIcon fontSize="inherit" />, accent: 'blue' as const },
    { key: 'verify', icon: <AssignmentOutlinedIcon fontSize="inherit" />, accent: 'neutral' as const },
    { key: 'assign', icon: <PersonOutlineRoundedIcon fontSize="inherit" />, accent: 'neutral' as const },
    { key: 'resolve', icon: <CheckCircleOutlineRoundedIcon fontSize="inherit" />, accent: 'orange' as const },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: compact ? 120 : 170,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 0, md: 1 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: '16%',
          right: '12%',
          top: '50%',
          height: 2,
          transform: 'translateY(-50%)',
          background: `linear-gradient(90deg, ${alpha(blue, 0.8)} 0%, ${alpha(blue, 0.55)} 48%, ${alpha(orange, 0.55)} 72%, ${alpha(orange, 0.9)} 100%)`,
          boxShadow: `0 0 24px ${alpha(blue, 0.45)}`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 26% 50%, ${alpha(blue, 0.18)} 0%, rgba(47,140,255,0) 22%),
            radial-gradient(circle at 78% 50%, ${alpha(orange, 0.18)} 0%, rgba(255,154,61,0) 24%)
          `,
          pointerEvents: 'none',
        }}
      />
      <Stack direction="row" spacing={{ xs: 1.2, md: 2.1 }} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
        <Box
          sx={{
            position: 'relative',
            width: compact ? 66 : 86,
            height: compact ? 66 : 86,
            borderRadius: 3,
            display: 'grid',
            placeItems: 'center',
            color: blue,
            background: `radial-gradient(circle at 35% 35%, ${alpha(blue, 0.28)} 0%, rgba(47,140,255,0.08) 55%, rgba(47,140,255,0) 100%)`,
            border: `1px solid ${alpha(blue, 0.32)}`,
            boxShadow: `0 0 32px ${alpha(blue, 0.22)}`,
            flexShrink: 0,
          }}
        >
          <VideocamOutlinedIcon sx={{ fontSize: compact ? 34 : 42 }} />
        </Box>
        <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center" sx={{ flex: 1, justifyContent: 'space-evenly' }}>
          {nodes.map((node, index) => (
            <Box
              key={node.key}
              sx={{
                position: 'relative',
                width: compact ? 40 : 52,
                height: compact ? 40 : 52,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: compact ? 18 : 22,
                color: node.accent === 'orange' ? orange : node.accent === 'blue' ? blue : 'rgba(230,239,255,0.9)',
                border: `1px solid ${alpha(node.accent === 'orange' ? orange : node.accent === 'blue' ? blue : '#cdd8ee', 0.34)}`,
                background: `radial-gradient(circle at 35% 35%, ${alpha(
                  node.accent === 'orange' ? orange : node.accent === 'blue' ? blue : '#cdd8ee',
                  0.2,
                )} 0%, rgba(255,255,255,0.02) 60%)`,
                boxShadow:
                  index === nodes.length - 1
                    ? `0 0 20px ${alpha(orange, 0.3)}`
                    : `0 0 18px ${alpha(blue, 0.18)}`,
                flexShrink: 0,
              }}
            >
              {node.icon}
            </Box>
          ))}
        </Stack>
        <Box
          sx={{
            width: compact ? 72 : 96,
            height: compact ? 72 : 96,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: orange,
            background: `radial-gradient(circle, ${alpha(orange, 0.28)} 0%, rgba(255,154,61,0.08) 58%, rgba(255,154,61,0) 100%)`,
            border: `1px solid ${alpha(orange, 0.35)}`,
            boxShadow: `0 0 46px ${alpha(orange, 0.3)}`,
            flexShrink: 0,
          }}
        >
          <CheckCircleOutlineRoundedIcon sx={{ fontSize: compact ? 38 : 48 }} />
        </Box>
      </Stack>
    </Box>
  );
};

export const AcceptanceDemoHero: React.FC<AcceptanceDemoHeroProps> = ({
  eyebrow,
  title,
  subtitle,
  contextLine,
  supportingText,
  illustration,
  compactTitle,
  badges,
  primaryAction,
  secondaryAction,
  tertiaryAction,
}) => (
  <AcceptanceDemoPanel tone="hero" sx={{ p: { xs: 2, md: 2.5 } }}>
    <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'stretch' }}
        spacing={{ xs: 2.5, md: 3 }}
      >
        <Box sx={{ maxWidth: 560, flex: '0 0 52%' }}>
          <AcceptanceDemoBadge label={eyebrow} accent="blue" />
          <Typography
            variant="h2"
            sx={{
              mt: 1.25,
              fontWeight: 900,
              letterSpacing: -0.04,
              lineHeight: 1.02,
              fontSize: compactTitle ? { xs: '2rem', sm: '2.4rem', lg: '2.9rem' } : { xs: '2.3rem', sm: '2.9rem', lg: '3.35rem' },
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              color: acceptanceDemoTokens.colors.textSecondary,
              fontWeight: 500,
              maxWidth: 520,
              lineHeight: 1.48,
              fontSize: { xs: '1rem', md: '1.05rem' },
            }}
          >
            {subtitle}
          </Typography>
          {contextLine ? (
            <Typography
              variant="body1"
              sx={{
                mt: 1.2,
                color: acceptanceDemoTokens.colors.textPrimary,
                fontWeight: 600,
                lineHeight: 1.45,
                fontSize: 13.5,
              }}
            >
              {contextLine}
            </Typography>
          ) : null}
          {supportingText ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.8,
                color: acceptanceDemoTokens.colors.textMuted,
                maxWidth: 540,
                lineHeight: 1.55,
                fontSize: 12.5,
              }}
            >
              {supportingText}
            </Typography>
          ) : null}

          {badges?.length ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ pt: 1.15 }}>
              {badges.map((badge) => (
                <AcceptanceDemoBadge
                  key={badge.label}
                  label={badge.label}
                  accent={badge.accent}
                  icon={badge.icon}
                  sx={{ py: 0.55, fontSize: 11.5 }}
                />
              ))}
            </Stack>
          ) : null}

          {(primaryAction || secondaryAction || tertiaryAction) ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ pt: 1.35 }}>
              {primaryAction ? (
                <AcceptanceDemoButton
                  label={primaryAction.label}
                  onClick={primaryAction.onClick}
                  accent={primaryAction.accent ?? 'orange'}
                  variant={primaryAction.variant ?? 'contained'}
                />
              ) : null}
              {secondaryAction ? (
                <AcceptanceDemoButton
                  label={secondaryAction.label}
                  onClick={secondaryAction.onClick}
                  accent={secondaryAction.accent ?? 'blue'}
                  variant={secondaryAction.variant ?? 'outlined'}
                />
              ) : null}
              {tertiaryAction ? (
                <AcceptanceDemoButton
                  label={tertiaryAction.label}
                  onClick={tertiaryAction.onClick}
                  accent={tertiaryAction.accent ?? 'neutral'}
                  variant={tertiaryAction.variant ?? 'text'}
                />
              ) : null}
            </Stack>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: { md: 340 },
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {illustration || <AcceptanceDemoFlowGraphic />}
        </Box>
      </Stack>
    </Stack>
  </AcceptanceDemoPanel>
);

export const AcceptanceDemoSectionHeader: React.FC<AcceptanceDemoSectionHeaderProps> = ({
  eyebrow,
  title,
  description,
  align = 'left',
  sx,
}) => (
  <Stack spacing={1} sx={sx} alignItems={align === 'center' ? 'center' : 'flex-start'}>
    <Typography
      variant="overline"
      sx={{
        color: acceptanceDemoTokens.colors.textMuted,
        fontWeight: 800,
        letterSpacing: 1.1,
        textAlign: align,
      }}
    >
      {eyebrow}
    </Typography>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 800,
        letterSpacing: -0.03,
        textAlign: align,
        maxWidth: 920,
      }}
    >
      {title}
    </Typography>
    {description ? (
      <Typography
        variant="body1"
        sx={{
          color: acceptanceDemoTokens.colors.textMuted,
          lineHeight: 1.65,
          maxWidth: 860,
          textAlign: align,
        }}
      >
        {description}
      </Typography>
    ) : null}
  </Stack>
);

export const AcceptanceDemoStatCard: React.FC<AcceptanceDemoStatCardProps> = ({
  eyebrow,
  title,
  detail,
  accent = 'orange',
  icon,
  ctaLabel,
  onClick,
  onCtaClick,
  minHeight = { xs: 168, md: 'auto' },
  sx,
}) => {
  const clickable = Boolean(onClick);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable || !onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      onClick={clickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      sx={{ height: '100%' }}
    >
      <AcceptanceDemoPanel
        tone={accent === 'neutral' ? 'default' : accent}
        sx={[
          {
            height: '100%',
            minHeight,
            cursor: clickable ? 'pointer' : 'default',
            transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
            '&:hover': clickable
              ? {
                  transform: 'translateY(-2px)',
                  borderColor: alpha(getAccentColor(accent), 0.4),
                }
              : undefined,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <Stack spacing={1.25} sx={{ height: '100%', justifyContent: 'space-between' }}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
              {eyebrow ? (
                <Typography
                  variant="overline"
                  sx={{
                    color: acceptanceDemoTokens.colors.textMuted,
                    fontWeight: 800,
                    letterSpacing: 0.95,
                  }}
                >
                  {eyebrow}
                </Typography>
              ) : (
                <span />
              )}
              {icon ? <AcceptanceDemoIconContainer accent={accent}>{icon}</AcceptanceDemoIconContainer> : null}
            </Stack>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                mt: 1.25,
                fontWeight: 800,
                letterSpacing: -0.02,
                lineHeight: 1.2,
                fontSize: { xs: '1.15rem', sm: '1.28rem' },
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.85,
                color: acceptanceDemoTokens.colors.textSecondary,
                lineHeight: 1.55,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {detail}
            </Typography>
          </Box>
          {ctaLabel ? (
            <AcceptanceDemoButton
              label={ctaLabel}
              onClick={onCtaClick || onClick || (() => undefined)}
              variant="text"
              accent={accent}
              sx={{ alignSelf: 'flex-start' }}
            />
          ) : null}
        </Stack>
      </AcceptanceDemoPanel>
    </Box>
  );
};
